/**
 * Reveal-on-scroll, built as a single shared IntersectionObserver instead of
 * one observer per element — pattern adapted from
 * `ColoredByte/Fidos-/assets/motion-coordinator.js`
 * (see docs/REFERENCE_AUDIT.md). None of Fidos's actual timing/easing
 * values were used — those come from Nantes' own duration/ease
 * tokens (see theme-tokens.css.liquid), applied purely via CSS classes
 * here; this file only decides *when* `.is-visible` gets added.
 *
 * Usage: add `data-motion` to any element. It receives `.is-visible` once
 * it scrolls into view, then is left alone (one-shot reveal, not a scroll-
 * jacking library).
 */

(function () {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const config = {
    threshold: 0.05,
    rootMargin: '0px 0px -5% 0px',
    maxActivePerTick: 12,
  };

  function revealAllImmediately() {
    document.querySelectorAll('[data-motion]').forEach((el) => el.classList.add('is-visible'));
  }

  function revealInViewport() {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    document.querySelectorAll('[data-motion]:not(.is-visible)').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < vh * 0.98 && rect.bottom > 0) {
        el.classList.add('is-visible');
      }
    });
  }

  if (prefersReduced || !('IntersectionObserver' in window)) {
    revealAllImmediately();
    return;
  }

  let queue = [];
  let ticking = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        queue.push(entry.target);
        observer.unobserve(entry.target);
      });
      if (!ticking && queue.length) {
        ticking = true;
        requestAnimationFrame(flushQueue);
      }
    },
    { threshold: config.threshold, rootMargin: config.rootMargin }
  );

  function flushQueue() {
    const batch = queue.splice(0, config.maxActivePerTick);
    batch.forEach((el) => el.classList.add('is-visible'));
    ticking = false;
    if (queue.length) requestAnimationFrame(flushQueue);
  }

  function observeNewMotionElements(root = document) {
    root.querySelectorAll('[data-motion]:not(.is-visible)').forEach((el) => observer.observe(el));
  }

  function start() {
    // Mark above-the-fold elements before enabling hide-until-visible styles,
    // so a failed observer never blanks the first paint.
    revealInViewport();
    document.documentElement.classList.add('motion-ready');
    observeNewMotionElements();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.NantesMotion = { observeNewMotionElements };
})();
